// EditRequest.js – fixed scope for handleRemoveExistingAttachment and small optimizations
import React, { useEffect, useRef, useState } from 'react';
import { Container, Form, Button, Row, Col, Card, ListGroup, Badge } from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import './RequestForApproval.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { FaTrash } from 'react-icons/fa';
import ApprovalProcessService from '../services/ApprovalProcessService';
import { useLoading } from '../Utils/LoadingContext';
import showToast from '../Utils/ToastContainer';
import { toast } from 'react-toastify';

const EditRequest = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { setLoading } = useLoading();
    const editorRef = useRef(null);
    const editorInstance = useRef(null);

    const editId = location.state?.id;
    if (editId === undefined) {
        navigate("/home");
    }
    const [approvalType, setApprovalType] = useState([]);
    const [formData, setFormData] = useState({
        Title: '',
        ApprovalTypeId: '',
        Description: '',
        Approver: '',
        CompanyName: '',
        DepartmentName: '',
        LcNumber: ''
    });
    const [selectedRequestId, setSelectedRequestId] = useState('');
    const [selectedRequests, setSelectedRequests] = useState([]);
    const [attachments, setAttachments] = useState([]);            // new uploads
    const [existingAttachments, setExistingAttachments] = useState([]); // already on server
    const [errors, setErrors] = useState({});
    const [attachmentError, setAttachmentError] = useState('');




    /* -------------------- helpers -------------------- */
    const handleRemoveExistingAttachment = async (index,fileName) => {
        
        const confirmed = window.confirm("Are you sure you want to delete the existing attachment?");
        if (confirmed) {
            try {
                const response = await ApprovalProcessService.DeleteFile(fileName);
                if (response.data.statusCode === 200)
                {
                    setExistingAttachments((prev) => prev.filter((_, i) => i !== index));
                    showToast("Your file hab been successfully deleted.", 'success', { autoClose: 700 });
                }
               
                
                // You may want to refresh the list or update state here
            } catch (error) {
                console.error("Error updating status:", error);
                alert("Failed to update status");
            }
        }
        
    };

    const handleRemoveRequest = (id) => {
        setSelectedRequests((prev) => prev.filter((r) => r.id !== id));
    };

    const handleAddRequest = () => {
        if (selectedRequestId && !selectedRequests.some((r) => r.id === selectedRequestId.id)) {
            setSelectedRequests([...selectedRequests, selectedRequestId]);
            setSelectedRequestId('');
            setErrors(prev => ({ ...prev, Approver: '' }));
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        const validExtensions = ['png', 'jpg', 'jpeg', 'pdf', 'xls', 'xlsx'];
        const invalid = files.filter((f) => !validExtensions.includes(f.name.split('.').pop().toLowerCase()));

        if (invalid.length) {
            const msg = `Invalid files: ${invalid.map((f) => f.name).join(', ')}`;
            toast.error(msg);
            setAttachmentError(msg);
        } else {
            setAttachmentError('');
            setAttachments((prev) => [...prev, ...files]);
        }
        e.target.value = null;
    };

    const loadApproverOptions = async (inputValue) => {
        if (!inputValue) return [];
        const res = await ApprovalProcessService.getApproverDropDownListData(inputValue);
        if (res.success) {
            return res.data.result.map((item) => ({ value: item.id, label: item.name, id: item.id, name: item.name }));
        }
        return [];
    };

    /* -------------------- initial data -------------------- */
    useEffect(() => {
        const initCKEditor = async () => {
            if (editorRef.current && !editorInstance.current) {
                editorInstance.current = await window.ClassicEditor.create(editorRef.current);

                // Clear error on typing
                editorInstance.current.model.document.on('change:data', () => {
                    const desc = editorInstance.current.getData();
                    const stripped = desc.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
                    if (stripped) {
                        setErrors((prev) => ({ ...prev, Description: '' }));
                    }
                });
            }
        };

        const fetchApproverData = async (id) => {
            try {
                const response = await ApprovalProcessService.getApproverDetails(id);
                if (response.success && response.data.result.length) {
                    const item = response.data.result[0];
                    return { id: item.id, name: item.name };
                }
            } catch (err) {
                console.error('fetchApproverData', err);
            }
            return { id, name: `Approver (${id})` };
        };

        const fetchData = async () => {
            setLoading(true);
            try {
                const [typeRes, reqRes] = await Promise.all([
                    ApprovalProcessService.getDropDownListData(2),
                    ApprovalProcessService.getRequestDetails(editId)
                ]);

                if (typeRes.success) setApprovalType(typeRes.data.result);
                if (reqRes.success && reqRes.data.result.length) {
                    const d = reqRes.data.result[0];
                    setFormData({
                        Title: d.title,
                        ApprovalTypeId: d.lcTypeId,
                        Description: d.description,
                        Approver: d.approverId,
                        LcNumber: d.lcNumber,
                        CompanyName: d.companyName,
                        DepartmentName: d.departmentName
                    });
                    setExistingAttachments(d.attachmentFilesList || []);
                    const approvers = await Promise.all(d.approverIds.map((id) => fetchApproverData(id)));
                    setSelectedRequests(approvers);
                    if (editorInstance.current) editorInstance.current.setData(d.description);
                }
            } catch (err) {
                console.error(err);
                toast.error('Failed to load data');
            } finally {
                setLoading(false);
            }
        };

        initCKEditor().then(fetchData);

        return () => {
            if (editorInstance.current) {
                editorInstance.current.destroy();
                editorInstance.current = null;
            }
        };
    }, [editId, setLoading]);



    const handleSubmit = async (e) => {
        e.preventDefault();
        const description = editorInstance.current?.getData() || '';
        // if (!validateForm(description)) return;
        const isValid = validateForm(description);
        if (isValid) {
            setLoading(true);
            try {
                const fd = new FormData();
                fd.append('id', editId);
                fd.append('title', formData.Title);
                fd.append('lCType', formData.ApprovalTypeId);
                fd.append('lCDescription', description);
                fd.append('stageId', 1);
                selectedRequests.forEach((a) => fd.append('approverIds', a.id));
                existingAttachments.forEach((fileName) =>
                    fd.append('existingFiles', fileName)
                );

                // ✅ Only send newly added files to be uploaded
                attachments.forEach((file) =>
                    fd.append('attachments', file)
                );

                const res = await ApprovalProcessService.UpdateApproval(fd);
                if (res.data.statusCode === 200) {
                    showToast('Request updated successfully', 'success');
                    navigate('/home');
                } else toast.error('Update failed');
            } catch (err) {
                toast.error('Something went wrong');
                console.error(err);
            } finally {
                setLoading(false);
            }
        }
    };
    const validateForm = (descriptionValue) => {
        let tempErrors = {};
        let isValid = true;



        const strippedDescription = descriptionValue.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
        if (!strippedDescription) {
            tempErrors.Description = 'Description is required';
            isValid = false;
        }

        if (selectedRequests.length === 0) {
            tempErrors.Approver = 'Please select at least one approver';
            isValid = false;
        }

        setErrors(tempErrors);
        return isValid;
    };

    /* -------------------- render -------------------- */
    return (
        <Container className="mt-5 request-form">
            <h6 className="fw-bold text-primary mb-2">
                Edit Request for Approval
                {formData.LcNumber && (
                    <span className="text-dark ms-2" style={{ fontSize: '0.95rem', fontWeight: 500 }}>
                        <Badge bg="secondary" className="me-2">#{formData.LcNumber}</Badge>
                    </span>
                )}
            </h6>


            <Card className="p-4 shadow-sm">
                <Form onSubmit={handleSubmit}>
                    {/* --- top row --- */}
                    <Row className="mb-3 align-items-end">
                        <Col md={4}>
                            <Form.Group controlId="ApprovalTypeId">
                                <Form.Label>Approval Type</Form.Label>
                                <Select
                                    name="ApprovalTypeId"
                                    options={approvalType.map(item => ({
                                        value: item.id,
                                        label: item.name
                                    }))}
                                    placeholder="Select Approval Type"
                                    value={approvalType.find(item => item.id === formData.ApprovalTypeId) ? {
                                        value: formData.ApprovalTypeId,
                                        label: approvalType.find(item => item.id === formData.ApprovalTypeId).name
                                    } : null}
                                    onChange={(selectedOption) =>
                                        handleChange({
                                            target: {
                                                name: 'ApprovalTypeId',
                                                value: selectedOption ? selectedOption.value : ''
                                            }
                                        })
                                    }
                                    isDisabled
                                    className={errors.ApprovalTypeId ? 'is-invalid' : ''}
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            height: 32, // Basic height
                                            minHeight: 32,
                                        }),
                                        dropdownIndicator: (base) => ({
                                            ...base,
                                            paddingTop: 2,
                                            paddingBottom: 2
                                        }),
                                        clearIndicator: (base) => ({
                                            ...base,
                                            paddingTop: 2,
                                            paddingBottom: 2
                                        }),
                                        valueContainer: (base) => ({
                                            ...base,
                                            paddingTop: 0,
                                            paddingBottom: 0
                                        })
                                    }}
                                />

                                <div style={{ minHeight: '1.25rem', marginTop: '0.25rem' }}>
                                    {errors.ApprovalTypeId && (
                                        <div className="invalid-feedback d-block">{errors.ApprovalTypeId}</div>
                                    )}
                                </div>
                            </Form.Group>
                        </Col>

                        <Col md={4} className="position-relative">
                            <Form.Group>
                                <Form.Label>Subject Line</Form.Label>
                                <div className="form-control bg-light">{formData.Title}</div>
                                {/* Reserve empty space to keep height consistent */}
                                <div style={{ minHeight: '1.25rem', marginTop: '0.25rem' }}></div>
                            </Form.Group>
                        </Col>


                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Company</Form.Label>
                                <div className="form-control bg-light">{formData.CompanyName}</div>
                                {/* Reserve empty space to keep height consistent */}
                                <div style={{ minHeight: '1.25rem', marginTop: '0.25rem' }}></div>
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Department & Approver search */}
                    <Row className="mb-3 align-items-end">
                        <Col md={4}>
                            <Form.Group className="mb-0">
                                <Form.Label>Department</Form.Label>
                                <div
                                    className="form-control bg-light d-flex align-items-center"
                                    style={{
                                        pointerEvents: 'none',
                                        height: '38px',
                                        minWidth: '100%',
                                    }}
                                >
                                    {formData.DepartmentName}
                                </div>
                            </Form.Group>
                        </Col>

                        <Col md={5}>
                            <Form.Group className="mb-0">
                                <Form.Label>Approver Stage</Form.Label>
                                <AsyncSelect
                                    cacheOptions
                                    loadOptions={loadApproverOptions}
                                    defaultOptions
                                    value={selectedRequestId}
                                    onChange={(option) => setSelectedRequestId(option)}
                                    placeholder="Search Approver By Name.."
                                    isClearable
                                    className="basic-single"
                                    classNamePrefix="select"
                                    styles={{
                                        control: (provided) => ({
                                            ...provided,
                                            minHeight: '38px',
                                            height: '38px',
                                        }),
                                        valueContainer: (provided) => ({
                                            ...provided,
                                            height: '38px',
                                            padding: '0 6px',
                                        }),
                                        input: (provided) => ({
                                            ...provided,
                                            margin: '0px',
                                        }),
                                        indicatorsContainer: (provided) => ({
                                            ...provided,
                                            height: '38px',
                                        }),
                                    }}
                                />
                                {errors?.Approver && (
                                    <div className="invalid-feedback d-block">{errors.Approver}</div>
                                )}
                            </Form.Group>
                        </Col>

                        <Col md={3}>
                            <Button
                                variant="success"
                                onClick={handleAddRequest}
                                disabled={!selectedRequestId}
                                style={{ width: '100px', height: '38px', marginTop: '8px' }}
                            >
                                Add
                            </Button>
                        </Col>
                    </Row>

                    {/* Approver list */}
                    {selectedRequests.length > 0 && (
                        <Row className="mb-3">
                            <Col>
                                <DragDropContext onDragEnd={({ source, destination }) => {
                                    if (!destination) return;
                                    const items = Array.from(selectedRequests);
                                    const [moved] = items.splice(source.index, 1);
                                    items.splice(destination.index, 0, moved);
                                    setSelectedRequests(items);
                                }}>
                                    <Droppable droppableId="approvers">
                                        {(provided) => (
                                            <ListGroup {...provided.droppableProps} ref={provided.innerRef}>
                                                {selectedRequests.map((it, idx) => (
                                                    <Draggable key={it.id} draggableId={it.id.toString()} index={idx}>
                                                        {(provided) => (
                                                            <ListGroup.Item ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} className="d-flex justify-content-between align-items-center">
                                                                Stage {idx + 1} - {it.name}
                                                                <Button variant="danger" size="sm" onClick={() => handleRemoveRequest(it.id)}><FaTrash /></Button>
                                                            </ListGroup.Item>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </ListGroup>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                            </Col>
                        </Row>
                    )}

                    {/* Description */}
                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <textarea ref={editorRef} className={`form-control ${errors.Description ? 'is-invalid' : ''}`} rows={8}></textarea>
                        {errors.Description && <div className="invalid-feedback">{errors.Description}</div>}
                    </Form.Group>

                    {/* Attachments */}
                    <Form.Group className="mb-3" controlId="attachments">
                        <Form.Label>Attachments (Optional)</Form.Label>
                        <Form.Control type="file" multiple onChange={handleFileChange} isInvalid={!!attachmentError} />
                        {attachmentError && <div className="text-danger small">{attachmentError}</div>}
                        {/* Existing */}
                        {existingAttachments.length > 0 && (
                            <div className="mt-3">
                                <h6 className="text-muted small mb-2">Existing Attachments</h6>
                                {existingAttachments.map((file, i) => (
                                    <div key={i} className="d-flex align-items-center gap-2 mb-1">
                                        <i className="bi bi-paperclip text-secondary"></i>
                                        <span className="text-truncate" style={{ maxWidth: '280px' }}>
                                            {file.substring(file.indexOf('_') + 1)}
                                        </span>
                                        <Button
                                            variant="link"
                                            onClick={() => handleRemoveExistingAttachment(i,file)}
                                            className="p-0 m-0 border-0 text-danger"
                                            style={{ width: '20px', height: '20px', lineHeight: '1' }}
                                        >
                                            <FaTrash size={14} />
                                        </Button>


                                    </div>
                                ))}
                            </div>
                        )}
                        {/* New */}
                        {attachments.length > 0 && (
                            <div className="mt-2">
                                <h6 className="text-muted small mb-2">New Attachments</h6>
                                {attachments.map((file, i) => (
                                    <div key={i} className="d-flex align-items-center gap-2 mb-1">
                                        <i className="bi bi-file-earmark-plus text-success"></i>
                                        <span>{file.name}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Form.Group>

                    <Button type="submit" className="w-100">Update</Button>
                </Form>
            </Card>
        </Container>
    );
};

export default EditRequest;
